// Field Ops READ family on NomOS (SEN-395 / TZ-2): assets, physical tasks, field-ops records.
// Reshape only: counts/totals come from NomOS pagination; nothing is aggregated client-side.
// Gaps that remain are thrown as NomosGapError (a visible failure) instead of being papered over with defaults.
import type {
  Asset, AssetFieldDefinition, AssetFieldDefinitions, AssetListResponse, AssetQuery, AssetType, AttachmentMeta,
  CommentRecord, Contact, FieldLink, FieldLinkIssued, GroupCounts, LinkRecord, PhysicalTask, PhysicalTaskListResponse,
  PhysicalTaskQuery, Visit,
} from '@/types';
import { registerTenantCache,
  getConfig, groupRecords, nomosFetch, queryRecords, scalar, toAxiosError, type NomosEnvelope, type NomosFilter,
} from '../client';
import { rememberId } from '../ids';
import type { AnyFn, NomosRegistry } from '../registry';

export class NomosGapError extends Error {
  constructor(message: string) { super(`NomOS gap: ${message}`); this.name = 'NomosGapError'; }
}
const notFound = (detail: string) => toAxiosError(404, { detail }, '');

// ---------- low-level reads (values are keyed by attribute_id on the wire; map back to slugs) ----------
interface AttrMeta { id: string; type: string; options?: string[]; name: string }
const attrCache = new Map<string, Promise<{ bySlug: Map<string, AttrMeta>; byId: Map<string, string> }>>();

function attrs(type: string) {
  const key = `${getConfig().baseUrl}|${getConfig().tenant}|${type}`;
  let p = attrCache.get(key);
  if (!p) {
    p = nomosFetch<NomosEnvelope<Array<{ attribute_id: string; slug: string; name: string; type: string; config?: { options?: Array<{ id: string }> } }>>>(
      'GET', `/objects/${encodeURIComponent(type)}/attributes`).then((r) => {
      const bySlug = new Map<string, AttrMeta>(); const byId = new Map<string, string>();
      for (const a of r.data) {
        bySlug.set(a.slug, { id: a.attribute_id, type: a.type, name: a.name, options: a.config?.options?.map((o) => o.id) });
        byId.set(a.attribute_id, a.slug);
      }
      return { bySlug, byId };
    });
    attrCache.set(key, p);
  }
  return p;
}

type Cells = Record<string, unknown>;
export type Row = { record_id: string; kenning: string; v: Cells };

export async function readCells(type: string, ids: string[], slugs: string[]): Promise<Map<string, Cells>> {
  const out = new Map<string, Cells>();
  if (!ids.length) return out;
  const [{ byId }, res] = await Promise.all([attrs(type), nomosFetch<NomosEnvelope<{ values?: Record<string, Cells> } | Record<string, Cells>>>(
    'POST', `/objects/${encodeURIComponent(type)}/records/values`, { record_ids: ids, attributes: slugs })]);
  const d = res.data as { values?: Record<string, Cells> };
  const vals = (d && typeof d === 'object' && 'values' in d && d.values ? d.values : (res.data as Record<string, Cells>)) ?? {};
  for (const [rid, cells] of Object.entries(vals)) {
    const m: Cells = {};
    for (const [k, v] of Object.entries(cells)) m[byId.get(k) ?? k] = v;
    out.set(rid, m);
  }
  return out;
}

export async function queryRows(type: string, slugs: string[], o: {
  filter?: NomosFilter; sorts?: Array<{ field: string; direction?: 'asc' | 'desc' }>; limit?: number; offset?: number;
}): Promise<{ rows: Row[]; total: number }> {
  // one read: records/query with the cells projected (client.queryRecords)
  const page = await queryRecords(type, { filter: o.filter, sorts: o.sorts, limit: o.limit ?? 50, cursor: o.offset ?? 0, fields: slugs });
  return { rows: page.rows.map((x) => ({ record_id: x.record_id, kenning: x.kenning, v: x.values as Cells })), total: page.total };
}

export async function getRow(type: string, rid: string, slugs: string[]): Promise<Row & { created_at?: string }> {
  const rec = await nomosFetch<NomosEnvelope<{ record_id: string; kenning: string; created_at?: string }>>(
    'GET', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(rid)}`);
  const cells = await readCells(type, [rid], slugs);
  return { record_id: rid, kenning: rec.data.kenning, created_at: rec.data.created_at, v: cells.get(rid) ?? {} };
}

// ---------- cell helpers ----------
const str = (x: unknown): string | null => { const s = scalar(x); return typeof s === 'string' && s !== '' ? s : null; };
const num = (x: unknown): number | null => { const s = scalar(x); return typeof s === 'number' ? s : typeof s === 'string' && s !== '' && !isNaN(Number(s)) ? Number(s) : null; };
function ref(x: unknown): { id: string; kenning: string } | null {
  const c = Array.isArray(x) ? x[0] : x;
  if (c && typeof c === 'object' && 'target_record_id' in (c as object)) {
    const o = c as { target_record_id: string; target_kenning?: string };
    return { id: o.target_record_id, kenning: o.target_kenning ?? '' };
  }
  return null;
}
const dateOnly = (x: unknown): string | null => { const s = str(x); return s ? s.slice(0, 10) : null; };

// ---------- Sentinel id <-> NomOS record helpers ----------
const SENTINEL_ATTR: Record<string, string> = { site: 'sentinel_building_id', initiative: 'sentinel_initiative_id', asset: 'sentinel_asset_id', task: 'sentinel_task_id' };
const toRecord = new Map<string, string>();   // `${type}:${sentinelId}` -> record_id
/** Sentinel id -> NomOS record id via the sentinel_*_id join key. Own resolver: reads values by attribute id
 *  (the shared ids.ts flush goes through client.queryRecords, whose values parsing does not match the wire). */
export async function resolveSid(type: 'site' | 'initiative' | 'asset' | 'task', sid: string): Promise<string | undefined> {
  const key = `${type}:${sid}`;
  if (toRecord.has(key)) return toRecord.get(key);
  const { rows } = await queryRows(type, [], { filter: { attribute: SENTINEL_ATTR[type], op: 'eq', value: sid }, limit: 1 });
  if (rows[0]) { toRecord.set(key, rows[0].record_id); return rows[0].record_id; }
  // A record created on NomOS has no Sentinel id: the UI holds its NomOS record id (toAssets/toTasks `id`).
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(sid)) {
    try {
      await nomosFetch('GET', `/objects/${encodeURIComponent(type)}/records/${encodeURIComponent(sid)}`);
      toRecord.set(key, sid);
      return sid;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status !== 404) throw e;
    }
  }
  return undefined;
}
/** record_id -> Sentinel id for a batch of referenced records of one type (reads the join-key attribute). */
export async function sentinelIdsOf(type: 'site' | 'initiative', recordIds: string[]): Promise<Map<string, string>> {
  const uniq = [...new Set(recordIds)];
  const attr = SENTINEL_ATTR[type];
  const cells = await readCells(type, uniq, [attr]);
  const out = new Map<string, string>();
  for (const [rid, c] of cells) { const s = str(c[attr]); if (s) out.set(rid, s); }
  return out;
}

type Kind = 'task' | 'intervention' | 'building' | 'asset';
const KIND_TYPE: Record<Kind, 'task' | 'initiative' | 'site' | 'asset'> = { task: 'task', intervention: 'initiative', building: 'site', asset: 'asset' };
export async function resolveTarget(kind: string, sentinelId: string): Promise<{ type: string; rid: string }> {
  if (!(kind in KIND_TYPE)) throw toAxiosError(422, { detail: `unknown record kind '${kind}'` }, '');
  const type = KIND_TYPE[kind as Kind];
  const rid = await resolveSid(type, sentinelId);
  if (!rid) throw notFound(`${kind} ${sentinelId} not found`);
  return { type, rid };
}

// ---------- people: every login is a person (Person.member, migration 164) ----------
const UUID_RX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type WireMemberId = { member_id: string; sentinel_user_id?: string | null };
/** A login's user id as the UI holds it (getUsers / the session user: sentinel_user_id ?? member_id) -> its member id. */
async function memberIdFor(userId: string): Promise<string | undefined> {
  const rows = await nomosFetch<WireMemberId[]>('GET', `/tenants/${encodeURIComponent(getConfig().tenant)}/members`, undefined, { tenantScoped: false });
  return rows.find((m) => m.sentinel_user_id === userId || m.member_id === userId)?.member_id;
}
/**
 * The UI's id for an assignee -> the Person record id. A person is addressed by its record id (getAssignees, a task's
 * assignee_user_id); a login (a user id: "My work", an old bookmark) resolves through its person's login link,
 * Person.member. Interim until the backfill (scripts/backfill_member_persons.py) has linked every login: an unlinked
 * migrated person whose sentinel_person_id is the Sentinel user id. Undefined: no such person.
 */
export async function personRecordFor(id: string): Promise<string | undefined> {
  if (UUID_RX.test(id)) {
    try {
      await nomosFetch('GET', `/objects/person/records/${encodeURIComponent(id)}`);
      return id;
    } catch (e) {
      if ((e as { response?: { status?: number } })?.response?.status !== 404) throw e;
    }
  }
  const member = await memberIdFor(id);
  const by = async (attribute: string, value: string) =>
    (await queryRows('person', [], { filter: { attribute, op: 'eq', value }, limit: 1 })).rows[0]?.record_id;
  return (member ? await by('member', member) : undefined) ?? await by('sentinel_person_id', id);
}

type WireMemberRow = WireMemberId & { email?: string; is_active?: boolean; is_person?: boolean };
/** A service principal's address (Sentinel's COO user, coo+<id>@coo.sentinelai.internal): `.internal` is a reserved,
 *  non-routable TLD, so no human logs in with it. TZ-1 imported such users as persons; they are not people. */
const isServiceAddress = (email: string | null) => !!email && /\.internal$/i.test(email.trim());

/**
 * The assignee picker: the PEOPLE (a person may have no login), by name.
 * - Persons only (person records, never logins), humans only: a person whose login is a service principal or is
 *   deactivated is left out, and so is an unlinked person with a service address.
 * - One entry per login (Person.member is unique; the first wins should two ever share it).
 * - Two people with the same name are told apart by their email when the caller may see emails (an admin: the
 *   members route returns emails only to admins).
 */
async function getAssignees(): Promise<Array<{ id: string; display_name: string }>> {
  const people: Array<{ id: string; name: string; email: string | null; member: string | null }> = [];
  const membersP = nomosFetch<WireMemberRow[]>('GET', `/tenants/${encodeURIComponent(getConfig().tenant)}/members`, undefined, { tenantScoped: false })
    .catch(() => [] as WireMemberRow[]);
  for (let offset = 0; ; offset += 500) {
    const { rows, total } = await queryRows('person', ['name', 'email', 'member'], { sorts: [{ field: 'name', direction: 'asc' }], limit: 500, offset });
    people.push(...rows.map((r) => ({ id: r.record_id, name: str(r.v.name) ?? r.kenning, email: str(r.v.email), member: ref(r.v.member)?.id ?? null })));
    if (!rows.length || offset + rows.length >= total) break;
  }
  const members = new Map((await membersP).map((m) => [m.member_id, m]));
  const seenMember = new Set<string>();
  const kept = people.filter((p) => {
    if (!p.member) return !isServiceAddress(p.email);
    if (seenMember.has(p.member)) return false;
    seenMember.add(p.member);
    const m = members.get(p.member);
    if (!m) return !isServiceAddress(p.email); // a login the caller's directory does not show (or archived): the person stands alone
    return m.is_person !== false && m.is_active !== false;
  });
  // a non-admin's members answer carries only their OWN email; an admin's carries everyone's
  const seesEmails = [...members.values()].filter((m) => typeof m.email === 'string' && m.email !== '').length > 1;
  const count = new Map<string, number>();
  for (const p of kept) count.set(p.name.toLowerCase(), (count.get(p.name.toLowerCase()) ?? 0) + 1);
  return kept.map((p) => ({
    id: p.id,
    display_name: seesEmails && p.email && (count.get(p.name.toLowerCase()) ?? 0) > 1 ? `${p.name} (${p.email})` : p.name,
  }));
}

// ---------- filters: Sentinel `<field>:<op>:<value>` -> NomOS filter ----------
// 'person': an assignee id as the UI holds it (a Person record id, or a login's user id -- personRecordFor).
// An id that is no person can be no task's assignee, so an eq on one matches nothing.
type FKind = 'text' | 'number' | 'bool' | 'site' | 'person';
/** A filter no record satisfies (the same form NomOS uses for a member who may see no site). */
export const MATCH_NOTHING: NomosFilter = { attribute: 'record_id', op: 'in', value: [] };
interface FieldSpec { path: string; kind: FKind }
const OPS: Record<string, string> = { eq: 'eq', ne: 'ne', gte: 'gte', lte: 'lte' };
async function parseFilters(filters: string[] | undefined, fields: Record<string, FieldSpec>, what: string): Promise<NomosFilter[]> {
  const out: NomosFilter[] = [];
  for (const f of filters ?? []) {
    const [field, op, ...rest] = f.split(':');
    const raw = rest.join(':');
    const spec = fields[field];
    if (!spec) throw new NomosGapError(`${what} filter '${field}' has no NomOS attribute`);
    const nop = OPS[op];
    if (!nop) throw new NomosGapError(`filter op '${op}' unsupported here`);
    if (spec.kind === 'site') {
      const rid = await resolveSid('site', raw);
      if (!rid) throw notFound(`building ${raw} not found`);
      out.push({ attribute: spec.path, op: nop, value: rid });
    } else if (spec.kind === 'person') {
      const rid = await personRecordFor(raw);
      if (rid) out.push({ attribute: spec.path, op: nop, value: rid });
      else if (nop === 'eq') out.push(MATCH_NOTHING);   // ne on an unknown person excludes nothing
    } else {
      const value = spec.kind === 'number' ? Number(raw) : spec.kind === 'bool' ? raw === 'true' : raw;
      out.push({ attribute: spec.path, op: nop, value });
    }
  }
  return out;
}
const siteFilter = async (op: string, v: string): Promise<NomosFilter> => {
  const rid = await resolveSid('site', v);
  if (!rid) throw notFound(`building ${v} not found`);
  return { attribute: 'site', op: op === 'ne' ? 'ne' : 'eq', value: rid };
};
const and = (cs: NomosFilter[]): NomosFilter | undefined => (cs.length ? (cs.length === 1 ? cs[0] : { and: cs }) : undefined);

/** records/query group_by -> Sentinel GroupCounts ({values:{field:value}, count}); total is NomOS's own record total. */
async function groupCounts(type: 'asset' | 'task', fields: Record<string, FieldSpec>, groupBy: string[], filter: NomosFilter | undefined): Promise<GroupCounts> {
  const specs = groupBy.map((g) => { const f = fields[g]; if (!f) throw new NomosGapError(`group_by '${g}' has no NomOS path`); return f; });
  const [groups, { total }] = await Promise.all([
    groupRecords(type, { groupBy: specs.map((f) => f.path), filter }),
    queryRows(type, [], { filter, limit: 1 }),
  ]);
  const idx = (k: FKind) => specs.map((f, i) => (f.kind === k ? i : -1)).filter((i) => i >= 0);
  const keysAt = (is: number[]) => groups.flatMap((g) => is.map((i) => g.key[i])).filter((k) => k != null).map(String);
  const siteIdx = idx('site');
  // a person group's key is the Person record id: the UI's assignee id as is
  const sites = siteIdx.length ? await sentinelIdsOf('site', keysAt(siteIdx)) : new Map<string, string>();
  return {
    group_by: groupBy,
    groups: groups.map((g) => ({ values: Object.fromEntries(groupBy.map((name, i) => {
      const k = g.key[i];
      const ids = specs[i].kind === 'site' ? sites : undefined;
      return [name, ids && k != null ? ids.get(String(k)) ?? k : k];
    })), count: g.count })),
    total,
  } as unknown as GroupCounts;
}

// =====================================================================================
// Assets
// =====================================================================================
export const ASSET_FIELDS = ['name', 'site', 'category', 'asset_type', 'installed', 'decommission_date', 'expected_lifespan_years', 'last_maintenance_date',
  'next_maintenance_due', 'manufacturer', 'model', 'serial_number', 'status', 'controllable', 'rated_power_kw', 'role', 'provenance', 'source',
  'confidence', 'basis', 'confirmed_by', 'confirmed_at', 'operating_schedule', 'quantity', 'zone', 'energy_source', 'control_type',
  'responsible_party', 'attributes', 'sentinel_asset_id'];
const ASSET_FIELD_MAP: Record<string, FieldSpec> = {
  name: { path: 'name', kind: 'text' }, manufacturer: { path: 'manufacturer', kind: 'text' }, model: { path: 'model', kind: 'text' },
  category: { path: 'category.category', kind: 'text' }, category_code: { path: 'category.code', kind: 'text' }, category_name: { path: 'category.name', kind: 'text' },
  status: { path: 'status', kind: 'text' }, provenance: { path: 'provenance', kind: 'text' }, source: { path: 'source', kind: 'text' }, zone: { path: 'zone', kind: 'text' },
  controllable: { path: 'controllable', kind: 'bool' }, building_id: { path: 'site', kind: 'site' }, building_name: { path: 'site.name', kind: 'text' },
  country_code: { path: 'site.country_ref.code', kind: 'text' }, rated_power_kw: { path: 'rated_power_kw', kind: 'number' },
  quantity: { path: 'quantity', kind: 'number' }, confidence: { path: 'confidence', kind: 'number' },
};
const ASSET_SORTS: Record<string, string> = { name: 'name', install_date: 'installed', manufacturer: 'manufacturer', model: 'model', status: 'status',
  provenance: 'provenance', source: 'source', zone: 'zone', rated_power_kw: 'rated_power_kw', quantity: 'quantity', confidence: 'confidence',
  building_name: 'site.name', category: 'category.category', category_code: 'category.code', category_name: 'category.name' };

const CATEGORY_FIELDS = ['code', 'category', 'name', 'energy_role', 'rated_power_kw', 'seasonal_pattern', 'sentinel_asset_type_id', 'default_energy_source'];
/** asset_category (the Sentinel asset-type catalogue) and energy_source codes: small tables, cached per session. */
let categoryCache: Promise<Map<string, Cells>> | undefined;
function assetCategories(): Promise<Map<string, Cells>> {
  categoryCache ??= queryRows('asset_category', CATEGORY_FIELDS, { limit: 1000 })
    .then((r) => new Map(r.rows.map((x) => [x.record_id, x.v])));
  categoryCache.catch(() => { categoryCache = undefined; });
  return categoryCache;
}
let energyCodeCache: Promise<Map<string, string>> | undefined;
/** Per-tenant state of this module, dropped when the tenant changes (B7). */
export function clearFieldopsCaches(): void { attrCache.clear(); toRecord.clear(); categoryCache = undefined; energyCodeCache = undefined; }
registerTenantCache(clearFieldopsCaches);
function energySourceCodes(): Promise<Map<string, string>> {
  energyCodeCache ??= queryRows('energy_source', ['code'], { limit: 1000 }).then((r) => new Map(r.rows.map((x) => [x.record_id, str(x.v.code) ?? ''])));
  energyCodeCache.catch(() => { energyCodeCache = undefined; });
  return energyCodeCache;
}

/** Meters that ARE each asset (meter.physical_asset), Sentinel's `meters` on a metering asset. Sorted by name as Sentinel does. */
async function metersOfAssets(assetIds: string[]): Promise<Map<string, Array<{ meter_id: string; meter_name: string; measures: never[] }>>> {
  const out = new Map<string, Array<{ meter_id: string; meter_name: string; measures: never[] }>>();
  for (let i = 0; i < assetIds.length; i += 200) {
    const chunk = assetIds.slice(i, i + 200);
    const { rows } = await queryRows('meter', ['name', 'sentinel_meter_id', 'physical_asset'], {
      filter: { attribute: 'physical_asset', op: 'in', value: chunk }, sorts: [{ field: 'name', direction: 'asc' }], limit: 1000 });
    for (const m of rows) {
      const a = ref(m.v.physical_asset); if (!a) continue;
      const sid = str(m.v.sentinel_meter_id);
      if (sid) rememberId('meter', sid, m.record_id);
      // `measures` is Sentinel's attribution (meter_seu_attribution), empty in the source: not surfaced (owner ruling)
      (out.get(a.id) ?? out.set(a.id, []).get(a.id)!).push({ meter_id: sid ?? m.record_id, meter_name: str(m.v.name) ?? m.kenning, measures: [] });
    }
  }
  return out;
}

export async function toAssets(rows: Row[]): Promise<Asset[]> {
  const [sites, cats, energy, meters] = await Promise.all([
    sentinelIdsOf('site', rows.map((r) => ref(r.v.site)?.id).filter((x): x is string => !!x)), assetCategories(), energySourceCodes(),
    metersOfAssets(rows.map((r) => r.record_id))]);
  return rows.map((r) => {
    const v = r.v;
    const sid = str(v.sentinel_asset_id);
    if (sid) rememberId('asset', sid, r.record_id);
    const site = ref(v.site); const cat = ref(v.category); const c = (cat && cats.get(cat.id)) || {};
    const es = ref(v.energy_source); const dflt = es ? null : ref(c.default_energy_source); // the type's carrier when none is recorded
    const attrs = (() => { const j = Array.isArray(v.attributes) ? v.attributes[0] : v.attributes; return j && typeof j === 'object' && !('value' in (j as object)) ? j as Record<string, unknown> : {}; })();
    const by = str(v.confirmed_by);
    return {
      id: sid ?? r.record_id,
      building_id: (site && sites.get(site.id)) || site?.id || '',
      name: str(v.name) ?? r.kenning,
      building_name: site?.kenning ?? null,
      category: str(c.category) ?? 'unknown',
      category_code: str(c.code), category_name: str(c.name) ?? cat?.kenning ?? null,
      category_id: (str(c.sentinel_asset_type_id) ?? cat?.id) ?? null,
      manufacturer: str(v.manufacturer), model: str(v.model), serial_number: str(v.serial_number),
      install_date: dateOnly(v.installed), decommission_date: dateOnly(v.decommission_date),
      expected_lifespan_years: num(v.expected_lifespan_years), last_maintenance_date: dateOnly(v.last_maintenance_date),
      next_maintenance_due: dateOnly(v.next_maintenance_due),
      status: str(v.status) ?? 'operational', controllable: scalar(v.controllable) === true, rated_power_kw: num(v.rated_power_kw),
      provenance: str(v.provenance) ?? 'declared', source: str(v.source), confidence: num(v.confidence), basis: str(v.basis),
      confirmed_by: by ? (by.includes(':') ? { kind: by.split(':')[0], id: by.slice(by.indexOf(':') + 1) } : { kind: 'user', id: by }) : null,
      confirmed_at: str(v.confirmed_at),
      quantity: num(v.quantity), zone: str(v.zone), energy_source: es ? energy.get(es.id) ?? es.kenning : dflt ? energy.get(dflt.id) ?? dflt.kenning : null,
      operating_schedule: str(v.operating_schedule), control_type: str(v.control_type), responsible_party: str(v.responsible_party),
      attributes: attrs, derived: {}, energy_source_is_default: !es && !!dflt,
      // the meter point link is Meter.physical_asset: the first linked meter (what the asset form edits);
      // measured load/consumption have no NomOS home
      meter_point_id: meters.get(r.record_id)?.[0]?.meter_id ?? null, measured_annual_kwh: null, measured_load_kw: null,
      // measured_by is Sentinel's attribution (empty in the source, owner ruling: not surfaced); meters = meter.physical_asset
      measured_by: [], meters: meters.get(r.record_id) ?? [],
    } as unknown as Asset;
  });
}

/** Sentinel scope: role (default consuming), the assets of active buildings when no building is given, has_meter. */
async function assetFilter(params: { filter?: string[]; building_id?: string; role?: string; has_meter?: boolean }): Promise<NomosFilter | undefined> {
  const cs = await parseFilters(params.filter, ASSET_FIELD_MAP, 'asset');
  cs.push({ attribute: 'role', op: 'eq', value: params.role ?? 'consuming' });
  if (params.building_id) cs.push(await siteFilter('eq', params.building_id));
  else cs.push({ attribute: 'site.is_active', op: 'eq', value: true });
  if (params.has_meter !== undefined) cs.push({ attribute: 'metering_roles', op: params.has_meter ? 'is_set' : 'is_not_set', value: true });
  return and(cs);
}

async function getAssets(params: AssetQuery = {}): Promise<AssetListResponse> {
  if (params.group_by?.length) return groupAssets(params as never) as unknown as AssetListResponse;
  const filter = await assetFilter(params as never);
  const sorts: Array<{ field: string; direction: 'asc' | 'desc' }> = [];
  const dir = params.order ?? 'asc';
  if (params.sort && ASSET_SORTS[params.sort]) sorts.push({ field: ASSET_SORTS[params.sort], direction: dir });
  for (const d of ['site.name', 'category.category', 'name']) if (!sorts.some((x) => x.field === d)) sorts.push({ field: d, direction: 'asc' });
  const wholeBuilding = !!params.building_id && params.limit === undefined && params.offset === undefined && !params.filter?.length && params.has_meter === undefined;
  const { rows, total } = await queryRows('asset', ASSET_FIELDS, {
    filter, limit: wholeBuilding ? 5000 : params.limit ?? 50, offset: params.offset ?? 0, sorts });
  const next = (params.offset ?? 0) + (wholeBuilding ? 0 : params.limit ?? 50);
  // Sentinel's opaque cursor for the page after this one (base64url "o:<offset>"), only when this page is paged and not the last
  const next_cursor = !wholeBuilding && params.limit !== undefined && next < total ? btoa(`o:${next}`).replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_') : null;
  return { data: await toAssets(rows), total, building_id: params.building_id ?? null, bacs: null, next_cursor };
}

async function groupAssets(params: { group_by: string[]; filter?: string[]; building_id?: string; role?: string; has_meter?: boolean }): Promise<GroupCounts> {
  return groupCounts('asset', ASSET_FIELD_MAP, params.group_by, await assetFilter(params));
}

async function getAssetTypes(): Promise<AssetType[]> {
  const { rows } = await queryRows('asset_category', CATEGORY_FIELDS, { limit: 1000, sorts: [{ field: 'code', direction: 'asc' }] });
  return rows.map((r) => ({ id: str(r.v.sentinel_asset_type_id) ?? r.record_id, code: str(r.v.code), category: str(r.v.category), name: str(r.v.name) ?? r.kenning,
    variant: null, rated_power_kw: num(r.v.rated_power_kw), capacity: null, energy_role: str(r.v.energy_role), seasonal_pattern: str(r.v.seasonal_pattern) }) as unknown as AssetType);
}

/** GET /assets/field-definitions: the FO-3 registry is NomOS reference data (asset_field_catalogue record + asset_category.field_definitions). */
async function getAssetFieldDefinitions(category?: string): Promise<AssetFieldDefinitions> {
  const cat = await queryRows('asset_field_catalogue', ['common_fields', 'gwp', 'gwp_basis', 'derived'], { limit: 1 });
  const c = cat.rows[0]?.v ?? {};
  const { rows } = await queryRows('asset_category', ['code', 'field_definitions'], { limit: 1000 });
  type Def = { energy_source?: string; end_use?: string; fields?: Array<{ key: string }> };
  let cats = rows.map((r) => ({ code: str(r.v.code) ?? '', def: r.v.field_definitions as Def | null | undefined })).filter((x) => x.def);
  if (category !== undefined) {
    cats = cats.filter((x) => x.code === category);
    if (!cats.length) throw new NomosGapError(`unknown asset category '${category}'`);
  }
  const used = new Map<string, unknown>();
  for (const x of cats) for (const f of x.def!.fields ?? []) if (!used.has(f.key)) used.set(f.key, f);
  return {
    common: (c.common_fields as AssetFieldDefinition[]) ?? [],
    categories: cats.map((x) => ({ code: x.code, energy_source: x.def!.energy_source, end_use: x.def!.end_use, fields: (x.def!.fields ?? []).map((f) => f.key) })),
    fields: [...used.values()],
    gwp: used.has('refrigerant') ? (c.gwp as Record<string, number>) ?? {} : {},
    gwp_basis: str(c.gwp_basis) ?? '', derived: (c.derived as string[]) ?? [],
  } as unknown as AssetFieldDefinitions;
}

// =====================================================================================
// Physical tasks
// =====================================================================================
export const TASK_FIELDS = ['display_id', 'name', 'description', 'task_type', 'priority', 'status', 'site', 'asset', 'initiative',
  'assigned_to', 'assignee', 'due_date', 'started_at', 'completed_at', 'completion_notes', 'outcome', 'verified_by', 'verified_at',
  'estimated_hours', 'actual_hours', 'estimated_cost_eur', 'actual_cost_eur', 'sentinel_task_id'];
const TASK_FIELD_MAP: Record<string, FieldSpec> = {
  title: { path: 'name', kind: 'text' }, task_type: { path: 'task_type', kind: 'text' }, status: { path: 'status', kind: 'text' },
  priority: { path: 'priority', kind: 'text' }, building_id: { path: 'site', kind: 'site' }, building_name: { path: 'site.name', kind: 'text' },
  country_code: { path: 'site.country_ref.code', kind: 'text' }, due_date: { path: 'due_date', kind: 'text' },
  display_number: { path: 'display_id', kind: 'number' },
  // Sentinel physical_tasks.assignee_user_id -> NomOS Task.assignee (a Person; sweep 2026-10-13 /work)
  assignee_user_id: { path: 'assignee', kind: 'person' },
};

export async function toTasks(rows: Row[], createdAt?: Map<string, string>): Promise<PhysicalTask[]> {
  const [sites, inits] = await Promise.all([
    sentinelIdsOf('site', rows.map((r) => ref(r.v.site)?.id).filter((x): x is string => !!x)),
    sentinelIdsOf('initiative', rows.map((r) => ref(r.v.initiative)?.id).filter((x): x is string => !!x)),
  ]);
  return rows.map((r) => {
    const sid = str(r.v.sentinel_task_id);
    if (sid) rememberId('task', sid, r.record_id);
    const site = ref(r.v.site); const init = ref(r.v.initiative); const v = r.v;
    const disp = str(v.display_id);
    return {
      id: sid ?? r.record_id,
      building_id: (site && sites.get(site.id)) || site?.id || '',
      building_name: site?.kenning ?? null,                      // the site reference's kenning: no extra read
      customer_id: '',                                           // gap: no customer on NomOS records (tenant-scoped)
      display_number: disp && /(\d+)$/.test(disp) ? Number(/(\d+)$/.exec(disp)![1]) : null,
      proposal_id: init ? inits.get(init.id) ?? init.id : null,
      equipment_id: null,                                        // Sentinel's equipment_id is orphaned (pack comment)
      task_type: str(v.task_type), title: str(v.name) ?? r.kenning, description: str(v.description),
      priority: str(v.priority) ?? 'medium', status: str(v.status) ?? 'pending',
      assigned_to: str(v.assigned_to), assignee_user_id: ref(v.assignee)?.id ?? null,   // the Person record id (getAssignees)
      due_date: dateOnly(v.due_date), started_at: str(v.started_at), completed_at: str(v.completed_at),
      completion_notes: str(v.completion_notes), completion_photos: null, outcome: str(v.outcome),
      verified_by: ref(v.verified_by)?.kenning ?? null, verified_at: str(v.verified_at),
      estimated_hours: num(v.estimated_hours), actual_hours: num(v.actual_hours),
      estimated_cost: num(v.estimated_cost_eur), actual_cost: num(v.actual_cost_eur),
      metadata: null, created_at: createdAt?.get(r.record_id) ?? '', updated_at: createdAt?.get(r.record_id) ?? '',
    } as unknown as PhysicalTask;
  });
}

async function taskFilter(params: { filter?: string[]; building_id?: string; status?: string; task_type?: string; assigned_to?: string }): Promise<NomosFilter | undefined> {
  const cs = await parseFilters(params.filter, TASK_FIELD_MAP, 'task');
  if (params.assigned_to) cs.push({ attribute: 'assigned_to', op: 'eq', value: params.assigned_to });
  if (params.building_id) cs.push(await siteFilter('eq', params.building_id));
  if (params.status) cs.push({ attribute: 'status', op: 'eq', value: params.status });
  if (params.task_type) cs.push({ attribute: 'task_type', op: 'eq', value: params.task_type });
  return and(cs);
}

async function listTasks(params: PhysicalTaskQuery = {}): Promise<PhysicalTaskListResponse> {
  if (params.group_by?.length) return groupTasks(params as never) as unknown as PhysicalTaskListResponse;
  const filter = await taskFilter(params);
  const sortMap: Record<string, string> = { due_date: 'due_date', status: 'status', priority: 'priority', title: 'name' };
  const sortField = params.sort ? sortMap[params.sort] : undefined;
  const { rows, total } = await queryRows('task', TASK_FIELDS, {
    filter, limit: params.limit ?? 50, offset: params.offset ?? 0,
    sorts: sortField ? [{ field: sortField, direction: params.order ?? 'asc' }] : undefined,
  });
  return { data: await toTasks(rows), total, building_id: params.building_id ?? null, status: params.status ?? null };
}

async function groupTasks(params: { group_by: string[]; filter?: string[]; building_id?: string; status?: string; task_type?: string }): Promise<GroupCounts> {
  return groupCounts('task', TASK_FIELD_MAP, params.group_by, await taskFilter(params));
}

async function getTask(id: string): Promise<PhysicalTask> {
  const rid = await resolveSid('task', id);
  if (!rid) throw toAxiosError(404, { detail: `Task ${id} not found` }, '');
  const row = await getRow('task', rid, TASK_FIELDS);
  return (await toTasks([row], new Map(row.created_at ? [[rid, row.created_at]] : [])))[0];
}

// =====================================================================================
// Field-ops records: comments, attachments, links
// =====================================================================================
const system = { kind: 'user' as const };
const actorId = (a: unknown): string => (a && typeof a === 'object' ? String((a as { id?: unknown; member_id?: unknown }).id ?? (a as { member_id?: unknown }).member_id ?? '') : String(a ?? ''));
const actorType = (a: unknown): string | null => (a && typeof a === 'object' ? str((a as { type?: unknown }).type) : null);
/** A comment's NomOS actor ({type, id}) as Sentinel's ActorOut: a member is a user, the agent is Sentinel's COO. */
const actorKind = (a: unknown): 'user' | 'coo' | 'system' => {
  const t = actorType(a);
  return t === 'system' || t === 'enrichment-provider' ? 'system' : t === 'agent' ? 'coo' : 'user';
};
/** member ids -> the person's name: the login's Person (Person.member) first, else the member's own name. */
async function authorNames(memberIds: string[]): Promise<Map<string, string>> {
  const ids = [...new Set(memberIds.filter((x) => UUID_RX.test(x)))];
  const out = new Map<string, string>();
  if (!ids.length) return out;
  const [persons, members] = await Promise.all([
    queryRows('person', ['name', 'member'], { filter: { attribute: 'member', op: 'in', value: ids }, limit: 1000 })
      .then((r) => r.rows).catch(() => [] as Row[]),
    nomosFetch<Array<WireMemberId & { display_name?: string | null; name?: string | null }>>(
      'GET', `/tenants/${encodeURIComponent(getConfig().tenant)}/members`, undefined, { tenantScoped: false }).catch(() => []),
  ]);
  for (const p of persons) { const m = ref(p.v.member)?.id; const n = str(p.v.name); if (m && n) out.set(m, n); }
  for (const m of members) {
    const n = m.display_name || m.name;
    if (!out.has(m.member_id) && n) out.set(m.member_id, n);
  }
  return out;
}
async function listComments(targetKind: string, targetId: string): Promise<CommentRecord[]> {
  const { type, rid } = await resolveTarget(targetKind, targetId);
  const res = await nomosFetch<NomosEnvelope<{ comments: Array<Record<string, unknown>> }>>(
    'GET', `/objects/${type}/records/${rid}/comments`);
  const comments = res.data.comments;
  const names = await authorNames(comments.flatMap((c) => [c.created_by, c.resolved_by])
    .filter((a) => a && actorKind(a) === 'user').map(actorId));
  const actor = (a: unknown) => { const id = actorId(a); const kind = actorKind(a); return { kind, id, name: kind === 'user' ? names.get(id) ?? null : null }; };
  return comments.map((c) => ({
    id: c.comment_id, customer_id: '', target_kind: targetKind, target_id: targetId,
    parent_id: c.parent_id ?? null, body: c.body, mentions: (c.mentions as string[]) ?? [], source: 'ui', email_id: null,
    created_by: actor(c.created_by), created_at: c.created_at,
    resolved_at: c.resolved_at ?? null, resolved_by: c.resolved_by ? actor(c.resolved_by) : null,
    archived_at: c.archived_at ?? null,
  }) as unknown as CommentRecord);
}

/** A triage item's attachments are the mail's own files, served on the triage row (ids `triage:<email_id>:<blob_id>`,
 *  the same shape getTriageItem returns and downloadAttachment resolves). Read-only: a triage item takes no uploads. */
async function listTriageAttachments(itemId: string): Promise<AttachmentMeta[]> {
  const d = (await nomosFetch<NomosEnvelope<{ email_id: string; created_at: string;
    attachments: Array<{ blob_id: string; filename: string; content_type: string; size_bytes: number; sha256: string }> }>>(
    'GET', `/field-ops/triage/${encodeURIComponent(itemId)}`)).data;
  return d.attachments.map((a) => ({
    id: `triage:${d.email_id}:${a.blob_id}`, customer_id: '', subject_kind: 'triage_item', subject_id: itemId, filename: a.filename,
    mime: a.content_type, bytes: a.size_bytes, sha256: a.sha256, created_by: { kind: 'email', id: d.email_id },
    created_at: d.created_at,
  }) as unknown as AttachmentMeta);
}

async function listAttachments(subjectKind: string, subjectId: string): Promise<AttachmentMeta[]> {
  if (subjectKind === 'triage_item') return listTriageAttachments(subjectId);
  const { type, rid } = await resolveTarget(subjectKind, subjectId);
  // NomOS Attachment hangs off Task only (class b/c in field-ops-mapping.md): other subjects are a gap.
  if (type !== 'task') throw new NomosGapError(`attachments on '${subjectKind}': NomOS Attachment has only a task reference`);
  const { rows } = await queryRows('attachment', ['name', 'storage_key', 'content_type', 'size_bytes', 'uploaded_at'],
    { filter: { attribute: 'task', op: 'eq', value: rid }, limit: 1000 });
  return rows.map((r) => ({
    id: r.record_id, customer_id: '', subject_kind: subjectKind, subject_id: subjectId,
    filename: str(r.v.name) ?? r.kenning, mime: str(r.v.content_type) ?? '', bytes: num(r.v.size_bytes) ?? 0,
    sha256: '',                                                  // gap: no checksum on Attachment
    created_by: { ...system, id: '' }, created_at: str(r.v.uploaded_at) ?? '',
  }) as unknown as AttachmentMeta);
}

/**
 * Links: Sentinel's generic (source, rel, target) table has no NomOS home (class c), BUT the only rel the UI uses
 * (`task.part_of`, task -> intervention) IS Task.initiative. Served from that reference in both directions.
 */
async function listRecordLinks(kind: string, recordId: string, rel?: string): Promise<LinkRecord[]> {
  const { type, rid } = await resolveTarget(kind, recordId);
  if (rel && rel !== 'task.part_of' && rel !== 'task.has_part') throw new NomosGapError(`links rel '${rel}': no NomOS home (generic link table is class c)`);
  const mk = (srcK: string, src: string, r: string, tgtK: string, tgt: string): LinkRecord => ({
    id: `${r}:${src}:${tgt}`, customer_id: '', source_kind: srcK, source_id: src, rel: r, target_kind: tgtK, target_id: tgt,
    created_by: { ...system, id: '' }, created_at: '',
  }) as unknown as LinkRecord;
  if (type === 'task') {
    const row = await getRow('task', rid, ['initiative']);
    const init = ref(row.v.initiative);
    if (!init) return [];
    const sid = (await sentinelIdsOf('initiative', [init.id])).get(init.id) ?? init.id;
    return [mk('task', recordId, 'task.part_of', 'intervention', sid)];
  }
  if (type === 'initiative') {
    const { rows } = await queryRows('task', ['sentinel_task_id'], { filter: { attribute: 'initiative', op: 'eq', value: rid }, limit: 1000 });
    return rows.map((r) => mk('intervention', recordId, 'task.has_part', 'task', str(r.v.sentinel_task_id) ?? r.record_id));
  }
  return [];
}

// =====================================================================================
// Contacts, field links, visits (NomOS types contact, field_link, field_visit; Sentinel ids in sentinel_*_id).
// =====================================================================================
export const CONTACT_FIELDS = ['name', 'email', 'phone', 'role', 'company', 'sentinel_contact_id'];
const contactSid = (r: Row) => str(r.v.sentinel_contact_id) ?? r.record_id;
async function contactRecordId(sentinelId: string): Promise<string | undefined> {
  const { rows } = await queryRows('contact', [], { filter: { attribute: 'sentinel_contact_id', op: 'eq', value: sentinelId }, limit: 1 });
  return rows[0]?.record_id ?? (/^[0-9a-f-]{36}$/.test(sentinelId) ? sentinelId : undefined);
}
export async function contactSentinelIds(recordIds: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>();
  for (const [rid, c] of await readCells('contact', [...new Set(recordIds)], ['sentinel_contact_id'])) { const s = str(c.sentinel_contact_id); if (s) out.set(rid, s); }
  return out;
}

async function listContacts(params?: { role?: string }): Promise<Contact[]> {
  const { rows } = await queryRows('contact', CONTACT_FIELDS, {
    filter: params?.role ? { attribute: 'role', op: 'eq', value: params.role } : undefined, limit: 1000 });
  return rows.map((r) => ({
    id: contactSid(r), customer_id: '', name: str(r.v.name) ?? r.kenning, email: str(r.v.email) ?? '', phone: str(r.v.phone),
    role: str(r.v.role), company: str(r.v.company), created_by: { ...system, id: '' }, created_at: '', archived_at: null,
  }) as unknown as Contact);
}

async function listFieldLinks(taskId: string): Promise<FieldLink[]> {
  const rid = await resolveSid('task', taskId);
  if (!rid) throw toAxiosError(404, { detail: `task ${taskId} not found` }, '');
  const { rows } = await queryRows('field_link', ['contact', 'expires_at', 'revoked_at', 'last_used_at', 'sentinel_field_link_id'],
    { filter: { attribute: 'task', op: 'eq', value: rid }, limit: 1000 });
  const contacts = await contactSentinelIds(rows.map((r) => ref(r.v.contact)?.id).filter((x): x is string => !!x));
  return rows.map((r) => {
    const c = ref(r.v.contact);
    return { id: str(r.v.sentinel_field_link_id) ?? r.record_id, customer_id: '', task_id: taskId, contact_id: (c && contacts.get(c.id)) ?? c?.id ?? '',
      expires_at: str(r.v.expires_at) ?? '', revoked_at: str(r.v.revoked_at), last_used_at: str(r.v.last_used_at), created_at: '' } as unknown as FieldLink;
  });
}

// ---------- field-link writes (office side; NomOS runtime in monad/api/v1/field_links.py) ----------
interface NomosFieldLink {
  id: string; customer_id?: string; task_id: string; contact_id: string; expires_at: string;
  revoked_at?: string | null; last_used_at?: string | null; created_at?: string; url?: string; emailed?: boolean;
}
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
async function linkRecordId(linkId: string): Promise<string> {
  const { rows } = await queryRows('field_link', [], { filter: { attribute: 'sentinel_field_link_id', op: 'eq', value: linkId }, limit: 1 });
  if (rows[0]) return rows[0].record_id;
  if (UUID_RE.test(linkId)) return linkId;
  throw notFound(`field link ${linkId} not found`);
}
/** NomOS link (record ids) -> Sentinel FieldLink. Pass the Sentinel ids you already know; the rest are read back. */
export async function reshapeFieldLink(d: NomosFieldLink, known: { taskId?: string; contactId?: string } = {}): Promise<FieldLink> {
  let taskId = known.taskId; let contactId = known.contactId;
  if (!taskId) taskId = str((await readCells('task', [d.task_id], ['sentinel_task_id'])).get(d.task_id)?.sentinel_task_id) ?? d.task_id;
  if (!contactId) contactId = (await contactSentinelIds([d.contact_id])).get(d.contact_id) ?? d.contact_id;
  const sid = str((await readCells('field_link', [d.id], ['sentinel_field_link_id'])).get(d.id)?.sentinel_field_link_id);
  return { id: sid ?? d.id, customer_id: d.customer_id ?? '', task_id: taskId, contact_id: contactId, expires_at: d.expires_at,
    revoked_at: d.revoked_at ?? null, last_used_at: d.last_used_at ?? null, created_at: d.created_at ?? '' } as FieldLink;
}

async function issueFieldLink(taskId: string, contactId: string): Promise<FieldLinkIssued> {
  const trid = await resolveSid('task', taskId);
  if (!trid) throw notFound(`task ${taskId} not found`);
  const crid = await contactRecordId(contactId);
  if (!crid) throw notFound(`contact ${contactId} not found`);
  rememberId('task', taskId, trid);
  const res = await nomosFetch<NomosEnvelope<NomosFieldLink>>('POST', `/field-ops/tasks/${encodeURIComponent(trid)}/field-links`, { contact_id: crid });
  const link = await reshapeFieldLink(res.data, { taskId, contactId });
  return { ...link, url: res.data.url ?? '', emailed: !!res.data.emailed };
}

async function revokeFieldLink(linkId: string): Promise<FieldLink> {
  const rid = await linkRecordId(linkId);
  const res = await nomosFetch<NomosEnvelope<NomosFieldLink>>('POST', `/field-ops/field-links/${encodeURIComponent(rid)}/revoke`);
  const link = await reshapeFieldLink(res.data);
  return { ...link, id: linkId };
}

async function getTaskVisit(taskId: string, contactId: string): Promise<Visit | null> {
  const rid = await resolveSid('task', taskId);
  if (!rid) throw toAxiosError(404, { detail: `task ${taskId} not found` }, '');
  const cid = await contactRecordId(contactId);
  if (!cid) return null;
  const { rows } = await queryRows('field_visit', ['field_link', 'checked_in_at', 'signed_off_at', 'signed_off_name', 'record_sha256', 'sentinel_field_visit_id'], {
    filter: { and: [{ attribute: 'task', op: 'eq', value: rid }, { attribute: 'contact', op: 'eq', value: cid }] }, limit: 1 });
  const r = rows[0];
  if (!r) return null;
  const link = ref(r.v.field_link);
  const linkSid = link ? str((await readCells('field_link', [link.id], ['sentinel_field_link_id'])).get(link.id)?.sentinel_field_link_id) : null;
  return { id: str(r.v.sentinel_field_visit_id) ?? r.record_id, field_link_id: linkSid ?? link?.id ?? '', task_id: taskId, contact_id: contactId,
    checked_in_at: str(r.v.checked_in_at), signed_off_at: str(r.v.signed_off_at), signed_off_name: str(r.v.signed_off_name),
    record_sha256: str(r.v.record_sha256) } as Visit;
}

export const fieldopsImpl: NomosRegistry = {
  getAssetTypes: getAssetTypes as unknown as AnyFn,
  getAssets: getAssets as unknown as AnyFn,
  groupAssets: groupAssets as unknown as AnyFn,
  getAssetFieldDefinitions: getAssetFieldDefinitions as unknown as AnyFn,
  listTasks: listTasks as unknown as AnyFn,
  getAssignees: getAssignees as unknown as AnyFn,
  groupTasks: groupTasks as unknown as AnyFn,
  getTask: getTask as unknown as AnyFn,
  listComments: listComments as unknown as AnyFn,
  listAttachments: listAttachments as unknown as AnyFn,
  listRecordLinks: listRecordLinks as unknown as AnyFn,
  listContacts: listContacts as unknown as AnyFn,
  listFieldLinks: listFieldLinks as unknown as AnyFn,
  issueFieldLink: issueFieldLink as unknown as AnyFn,
  revokeFieldLink: revokeFieldLink as unknown as AnyFn,
  getTaskVisit: getTaskVisit as unknown as AnyFn,
};
